import { useState } from 'react';
import { TextInput, Alert, Button, Title, Text, Stack } from '@mantine/core';
import { AlertOctagonIcon, MailIcon } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { sendEmployeePasswordResetLink } from '@/cloudFunctions/auth';
import AuthTermsDisclaimer from './AuthTermsDisclaimer';
import AuthLink from './AuthLink';

type FormState = { email: string } & (
  | { status: 'idle' }
  | { status: 'sending' }
  | { status: 'sent' }
  | { status: 'error'; message: string }
);

export const ForgotPasswordForm = () => {
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || '';
  const [state, setState] = useState<FormState>({
    status: 'idle',
    email,
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (state.status === 'sending') return;

    setState({ status: 'sending', email: state.email });

    try {
      const result = await sendEmployeePasswordResetLink({
        emailAddress: state.email,
      });

      if (result.status === 'success') {
        setState({ status: 'sent', email: state.email });
      } else {
        setState({
          status: 'error',
          message:
            result.userMessage ||
            'Failed to send reset link. Please try again.',
          email: state.email,
        });
      }
    } catch (error) {
      console.error('Failed to send reset link:', error);
      setState({
        status: 'error',
        message: 'Failed to send reset link. Please try again.',
        email: state.email,
      });
    }
  };

  if (state.status === 'sent') {
    return (
      <>
        <Title order={1} ta='center'>
          Check your email
        </Title>
        <Stack>
          <Alert color='green' radius='md'>
            We've sent an email to {state.email} with instructions to reset your
            password.
          </Alert>
          <Text size='sm' ta='center'>
            <Link to={`login`}>Return to sign in</Link>
          </Text>
        </Stack>
      </>
    );
  }

  const isLoading = state.status === 'sending';
  const errorMessage = state.status === 'error' ? state.message : '';

  return (
    <Stack>
      <Title order={1} ta='center'>
        Reset password
      </Title>
      <form onSubmit={handleSubmit}>
        <Stack>
          <TextInput
            type='email'
            name='email'
            placeholder='you@example.com'
            value={state.email}
            onChange={(e) => setState({ ...state, email: e.target.value })}
            label='Email'
            leftSection={<MailIcon size={16} />}
            size='md'
            radius='md'
            required
          />
          {errorMessage && (
            <Alert
              color='red'
              radius='md'
              icon={<AlertOctagonIcon size={16} />}
            >
              {errorMessage}
            </Alert>
          )}
          <Button
            type='submit'
            loading={isLoading}
            disabled={isLoading}
            fullWidth
          >
            Get a Reset Link
          </Button>
        </Stack>
      </form>
      <Text size='sm' ta='center'>
        Remember your password?{' '}
        <AuthLink to={`/login?email=${state.email}`}>Sign in</AuthLink>
      </Text>
      <AuthTermsDisclaimer />
    </Stack>
  );
};

export default ForgotPasswordForm;
